import {catchError, debounceTime, map, switchMap} from 'rxjs/operators';
import {EMPTY, Subject, from} from 'rxjs';
import {Component, OnInit, Input, OnChanges, OnDestroy} from '@angular/core';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {FeatureSwitchService, RateService} from '../../../services';
import {LCA_ORIGINATOR_PARTY_ROLE_ID} from '../../../_global/wiz.constants';
import {DataHandlerService} from '../../../services/data-handler.service';

@Component({
  selector: 'app-rate',
  templateUrl: './rate.component.html',
  standalone: true
})
export class RateComponent extends WizardWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type: String = 'motor-vehicle'; // set as motor-vehicle by default
  @Input() originator: any;
  @Input() baseform: any;
  @Input() bidCompliance = false;
  public curParams = '';
  public subscriptions = [];
  public dialDownDisabled = false;
  public realEstateCountWithoutNas = 0;
  public assetBackDiscountFeatureEnabled = false;

  /**
   * Queue of dial-down requests. Piped through switchMap so that only the most recently
   * requested rate call can emit — several triggers (asset change, discount eligibility,
   * valueChanges, homeowner) commonly fire within milliseconds of each other and, without
   * cancellation, whichever HTTP response happened to land last would win. On PROD latency
   * is both higher and more variable than in lower environments, so that ordering is not
   * stable and a stale response could overwrite the correct rates.
   */
  private dialDownTrigger = new Subject<{ type: any, data: any, patchOutput: boolean }>();

  /**
   * Resolves the asset-back-discount feature switch. Held as a promise (rather than awaited
   * before wiring subscriptions) so that no emission is missed while the switch is in flight.
   */
  private featureReady: Promise<boolean>;

  constructor(private rateService: RateService,
              public dataHandlerService: DataHandlerService,
              private featureSwitchService: FeatureSwitchService) {
    super(dataHandlerService);
    
  }

  ngOnChanges(changes) {
    if (changes.originator && changes.originator.currentValue) {
      if (changes.originator.currentValue.id === LCA_ORIGINATOR_PARTY_ROLE_ID) {
        this.formGroup.patchValue({channel: 'retail'});
      } else {
        this.formGroup.patchValue({channel: 'third-party'});
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.dialDownTrigger.complete();
  }

  ngOnInit() {
    super.ngOnInit();

    // Start the feature-switch lookup but do NOT await it before wiring up the
    // subscriptions below. Awaiting here meant every emission that arrived while the
    // 'feature-switches' request was still in flight was lost: BehaviorSubject only
    // replays its *latest* value to a late subscriber, and any intermediate
    // sendAssetChange()/sendDiscountEligibility() call was dropped entirely. That request
    // is cached in lower environments after the first widget resolves it (TypesService
    // memoises via loadedTypes), which is why the gap only shows up on PROD, where the
    // extra latency widens the window enough for producers to fire inside it.
    this.featureReady = this.featureSwitchService
      .isFeatureEnabled('fits-2511_clp-workbook-updates')
      .then((enabled: boolean) => {
        this.assetBackDiscountFeatureEnabled = enabled;
        return enabled;
      });

    // Single serialised path for every dial-down request, so a slow earlier response can
    // never overwrite a newer one.
    this.subscriptions.push(
      this.dialDownTrigger.pipe(
        switchMap(({type, data, patchOutput}) =>
          this.buildDialDownParams(data).pipe(
            switchMap((params: any) =>
              this.rateService.getDialDownRates(type, params).pipe(
                map((res: any) => ({res, patchOutput}))
              )
            ),
            // Keep the outer subscription alive: a failure on one request must not stop
            // later triggers from being served.
            catchError((err) => {
              console.error('Dial down rate request failed', err);
              return EMPTY;
            })
          )
        )
      ).subscribe(({res, patchOutput}) => {
        this.dataHandlerService.sendDialDownData(res);
        if (patchOutput && this.formGroup.get('output')) {
          this.formGroup.get('output').patchValue(res, {emitEvent: false});
        }
      })
    );

    this.subscriptions.push(this.dataHandlerService.getAssetChange()
      .subscribe(async (data: any) => {
        const realEstateCountWithoutNas = await this.calculateHomeOwnership();
        this.dataHandlerService.sendRealEstateCountWithoutNas(realEstateCountWithoutNas);

        // Wait for the switch here instead of before subscribing — the emission is already
        // captured, so nothing is missed regardless of how long the lookup takes.
        if (await this.featureReady) {
          const discountEligibility = await this.getDiscountEligibility();
          this.patchIfPresent('isDiscountEligible', discountEligibility);
          this.dataHandlerService.sendDiscountEligibility(discountEligibility);
        }

        if (!this.dialDownDisabled) {
          this.requestDialDownRates(this.formGroup.value);
        }
      }));

    this.subscriptions.push(this.dataHandlerService.getDiscountEligibility().subscribe((eligible) => {
      if (eligible === null) return;

      if (!this.dialDownDisabled) {
        // Guarded: on a partial/further-advance load the rate widget can be constructed
        // before 'isDiscountEligible' exists on the form group, and the unguarded
        // .get(...).patchValue() threw a TypeError that killed this subscription for the
        // rest of the session — so no later eligibility change ever refreshed the rates.
        this.patchIfPresent('isDiscountEligible', eligible);
        this.requestDialDownRates(this.formGroup.value);
      }
    }));

    this.subscriptions.push(this.dataHandlerService.getDialDownDisabledFlag().subscribe(async (result: any) => {
      this.dialDownDisabled = result;

      const currentParams = this.formGroup.value;
      currentParams.realEstateCountWithoutNas = await this.calculateHomeOwnership();

      if (await this.featureReady) {
        currentParams.discountEligibility = await this.getDiscountEligibility();
      }

      // initialize by retrieving at the start
      const hasOutput = this.formGroup.value.output && Object.keys(this.formGroup.value.output).length > 0;
      if (hasOutput) {
        this.rateService.setRateData(this.formGroup.value.output);
      } else if (!hasOutput && currentParams && !this.dialDownDisabled) {
        this.requestDialDownRates(currentParams);
      }
    }));

    // if any of the rate parameters change ---
    this.subscriptions.push(this.rateService
      .rateParameters
      .subscribe((val) => {
        const fgVal = this.formGroup.value;
        let newVal = fgVal;
        if (val) {
          newVal = Object.assign({}, fgVal, val);
          this.formGroup.patchValue(newVal);
        }
      }));

    this.subscriptions.push(this.formGroup.valueChanges.pipe(debounceTime(500)).subscribe(async (val) => {
      if (JSON.stringify(val) === this.curParams) {
        return;
      } // make sure there are updates before calling dial down endpoint
      
      this.curParams = JSON.stringify(val);
      if (!this.dialDownDisabled) {
        this.requestDialDownRates(val);
      }
    }));

    this.subscriptions.push(this.dataHandlerService.getIsHomeOwner().subscribe((isHomeowner) => {
      this.patchIfPresent('isHomeowner', isHomeowner);
      this.requestDialDownRates(this.formGroup.value);
    }));
  }

  /**
   * Queues a dial-down rate request. Supersedes any in-flight request.
   */
  private requestDialDownRates(data: any) {
    this.dialDownTrigger.next({
      type: this.type,
      data,
      patchOutput: !!this.formGroup.get('output')
    });
  }

  /**
   * Patches a control only when it exists on the form group. Widget models vary between
   * loan types and partial loads, so a missing control must not throw — an uncaught error
   * inside a subscribe() handler permanently tears down that subscription.
   */
  private patchIfPresent(controlName: string, value: any) {
    const control = this.formGroup.get(controlName);
    if (control) {
      control.patchValue(value);
    }
  }

  /**
   * Enriches the raw form values with the derived fields the pricing endpoint requires.
   */
  private buildDialDownParams(data: any) {
    return from(this.calculateHomeOwnership()).pipe(
      map((realEstateCountWithoutNas: any) => {
        const params = {...data};

        if (this.bidCompliance) {
          params.bidCompliance = true;
        }

        params.realEstateCountWithoutNas = realEstateCountWithoutNas;
        this.dataHandlerService.sendRealEstateCountWithoutNas(realEstateCountWithoutNas);
        params.classType = this.dataHandlerService.getVehicleTypeValue();

        return params;
      })
    );
  }

  /**
   * Requests dial-down rates. Retained for callers outside this component; internally the
   * trigger subject is used so concurrent requests are serialised.
   */
  getDialDownRates(type, data: any, patchOutput = false) {
    this.dialDownTrigger.next({type, data, patchOutput});
  }

  calculateHomeOwnership(): Promise<any> {
    // Resolution used to happen only from inside the forEach, on the last key. If the form
    // had no keys yet the promise never settled, and because every dial-down call awaits
    // this, the rate request was silently dropped. Counting up front and returning once
    // makes that impossible.
    return new Promise<any>((resolve) => {
      const rawValues = (this.baseform) ? this.baseform.getRawValue() : {};
      let homeAssetCount = 0;
      let investmentCount = 0;

      Object.keys(rawValues || {}).forEach((key: string) => {
        const step = rawValues[key];
        if (!step) {
          return;
        }

        const assetData: any[] = (step['app-assets-details'] && step['app-assets-details'].assetsList) || [];
        assetData
          .filter((x: any) => x && x.type === 'home')
          .forEach((home: any) => {
            const parties: any[] = home.parties || [];
            if (parties.find((x: any) => !x.nonApplicant && x.ownership > 0)) {
              homeAssetCount = homeAssetCount + 1;
            }
          });

        const investmentData: any[] = (step['app-investment-income'] && step['app-investment-income'].investmentList) || [];
        investmentData.forEach((investment: any) => {
          const parties: any[] = (investment && investment.parties) || [];
          if (parties.find((x: any) => !x.nonApplicant && x.ownership > 0)) {
            investmentCount = investmentCount + 1;
          }
        });
      });

      resolve(homeAssetCount + investmentCount);
    });
  }
  
  // Function to get discount eligibility
  async getDiscountEligibility() {
    const rawValues = (this.baseform) ? this.baseform.getRawValue() : {};

    // Scenario 1: Using vxApplication ID
    if (rawValues.vxApplication) {
      try {
        const response = await this.rateService.getDiscountEligibilityByAppId(rawValues.vxApplication);
        return (response) ? response.eligible : false;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    // Scenario 2: Using assets data
    let assetData: any[] = [];
    Object.keys(rawValues || {}).forEach((key) => {
      const step = rawValues[key];
      const assets: any[] = (step && step['app-assets-details'] && step['app-assets-details'].assetsList) || [];
      // Include asset only if it has at least one party that is not a non-applicant with ownership
      const applicantAssets = assets.filter((asset: any) =>
        asset && asset.parties && asset.parties.some((party: any) => !party.nonApplicant && party.ownership > 0)
      );
      assetData = [...assetData, ...applicantAssets];
    });

    try {
      const response = await this.rateService.getDiscountEligibilityByAssets(assetData);
      return (response) ? response.eligible : false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
